import { router } from 'expo-router';
import { useAtomValue } from 'jotai';
import * as React from 'react';
import { useState } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    CircleAlertIcon,
    Icon,
    Shadows,
    SplitIcon,
    SuperscriptRegular,
    View,
    XStack,
    YStack,
} from 'smoothie';

import useGetAllBankAccountDetails from '../../hooks/useGetAllBankAccountDetails';
import { type TransformedPaycheckRoutingConfig } from '../../state/PaycheckRoutingAtoms';
import { bankAccountDisplayDetails } from '../../state/SpacesAtoms';
import {
    PaycheckRoutingConfigStatus,
    type PaycheckRoutingIncomeDetail,
    PaycheckRoutingStatus,
} from '../../types/PaycheckRouting';
import {
    getClosedAccountsInConfig,
    paycheckHasActiveRoutingConfig,
} from '../paycheck-routing/helpers';
import NeedMoreAccountsForPaycheckRoutingSheet from '../paycheck-routing/NeedMoreAccountsForPaycheckRoutingSheet';

interface PaycheckSplitProps {
    paycheckRoutingConfigs?: TransformedPaycheckRoutingConfig[];
    paychecks?: PaycheckRoutingIncomeDetail[];
    accountId: string;
}
export const PaycheckSplit = ({
    paycheckRoutingConfigs,
    paychecks,
    accountId,
}: PaycheckSplitProps) => {
    const [isNeedMoreAccountsSheetOpen, setIsNeedMoreAccountsSheetOpen] =
        useState(false);
    const eligiblePaychecks = paychecks?.filter((paycheck) => {
        const hasActiveRoutingConfig =
            paycheckRoutingConfigs &&
            paycheckHasActiveRoutingConfig(paycheck, paycheckRoutingConfigs);

        return paycheck.accountId === accountId && !hasActiveRoutingConfig;
    });
    const accounts = useAtomValue(bankAccountDisplayDetails);
    const hasAtLeastTwoFruitfulCashAccounts = accounts.length > 1;
    const { closedAccountDetails } = useGetAllBankAccountDetails(false);

    const renderPaycheckItem = (
        originator: string,
        color: string,
        backgroundColor: string,
        statusText: PaycheckRoutingConfigStatus,
        key: string,
        needsAttention?: boolean,
    ) => (
        <XStack
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            key={key}
        >
            <XStack gap={8} alignItems="center">
                <BodyMediumRegular>{originator}</BodyMediumRegular>
                {needsAttention && (
                    <Icon icon={CircleAlertIcon} color="$orange900" size={18} />
                )}
            </XStack>
            <View
                backgroundColor={backgroundColor}
                paddingVertical={4}
                paddingHorizontal={8}
                borderRadius={999}
            >
                <SuperscriptRegular color={color}>
                    {statusText}
                </SuperscriptRegular>
            </View>
        </XStack>
    );

    return (
        <View
            borderWidth={1}
            borderColor="$gray100"
            backgroundColor="$white"
            borderRadius={20}
            style={Shadows.SMALL}
            padding={20}
            onPress={() => {
                if ((paycheckRoutingConfigs ?? []).length) {
                    router.navigate(
                        '/banking/paycheck-routing/account-detection',
                    );
                } else if (hasAtLeastTwoFruitfulCashAccounts) {
                    router.navigate('/banking/paycheck-routing/info');
                } else {
                    setIsNeedMoreAccountsSheetOpen(true);
                }
            }}
            hoverStyle={{ cursor: 'pointer' }}
        >
            <XStack flex={1} justifyContent="space-between" width="100%">
                <YStack gap={6} width="100%">
                    <XStack
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                    >
                        <BodyMediumEmphasized>
                            Paycheck split
                        </BodyMediumEmphasized>
                        <View rotate="180deg">
                            <Icon
                                icon={SplitIcon}
                                size={16}
                                color="$gray1000"
                            />
                        </View>
                    </XStack>
                    {(paycheckRoutingConfigs ?? []).map((routingConfig, i) => {
                        let color = '$green1000';
                        let backgroundColor = '$green300';
                        let statusText = PaycheckRoutingConfigStatus.ENABLED;

                        const needsAttention =
                            getClosedAccountsInConfig({
                                config: routingConfig,
                                closedAccountDetails,
                            }).length > 0;
                        if (
                            routingConfig?.status ===
                            PaycheckRoutingStatus.INACTIVE
                        ) {
                            color = '$gray1100';
                            backgroundColor = '$gray200';
                            statusText = PaycheckRoutingConfigStatus.DISABLED;
                        }
                        return renderPaycheckItem(
                            routingConfig.originator,
                            color,
                            backgroundColor,
                            statusText,
                            `routingConfig-${i}`,
                            needsAttention,
                        );
                    })}
                    {(eligiblePaychecks ?? []).map((paycheck, i) => {
                        const color = '$gray1100';
                        const backgroundColor = '$gray200';

                        return renderPaycheckItem(
                            paycheck.originator,
                            color,
                            backgroundColor,
                            PaycheckRoutingConfigStatus.ELIGIBLE,
                            `routingConfig-${i}`,
                        );
                    })}
                </YStack>
            </XStack>
            <NeedMoreAccountsForPaycheckRoutingSheet
                isOpen={isNeedMoreAccountsSheetOpen}
                onClose={() => {
                    setIsNeedMoreAccountsSheetOpen(false);
                }}
                onSubmit={() => {
                    setIsNeedMoreAccountsSheetOpen(false);
                    router.navigate('/banking/onboarding/space-ownership');
                }}
            />
        </View>
    );
};
